import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IDropdownMenu } from "./DropdownMenuInterface";
import { SearchBar } from "../SearchBar/SearchBar.view";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useStyles } from "./DropdownMenu.styles";
import { Avatar, Checkbox } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { allElements } from "constants/constants";

const MIN_OPTIONS_TO_SHOW_SEARCH_BAR = 9;
const multipleDefaultValueString = "$$multiple_value_default$$";

export const DropdownMenu = ({
    dropdownValue,
    handleChange,
    options,
    tKeys,
    withPicture,
    withSearchBar,
    hideTitle = false,
    disabled = false,
    fullWidth = false,
    bigSize,
    className,
    avatarSize = 42,
    searchBarClassName,
    multiple,
}: IDropdownMenu) => {
    const { t } = useTranslation();
    const { base, title, optionsHolder, key, placeholder } = tKeys;
    const [searchQuery, setSearchQuery] = useState("");
    const classes = useStyles();

    const handleCleanQuery = useCallback(() => {
        if (withSearchBar) {
            setSearchQuery("");
        }
    }, [withSearchBar]);

    if (multiple) {
        return (
            <Box className={classes.dropdownMenuContainer}>
                {!hideTitle && (
                    <InputLabel id="dropdown-select-label" className={classes.inputLabel}>
                        <Typography variant={bigSize ? "h5" : "subtitle2"}>
                            {t(`${base}.${title}`).toUpperCase()}
                        </Typography>
                    </InputLabel>
                )}
                <Select
                    multiple={true}
                    disabled={disabled}
                    displayEmpty={!!placeholder}
                    labelId="dropdown-select-label"
                    id="dropdown-select"
                    className={`${classes.select} ${className || ""}`}
                    value={dropdownValue && dropdownValue.length === 0 ? [multipleDefaultValueString] : dropdownValue}
                    onChange={handleChange}
                    IconComponent={KeyboardArrowDownOutlinedIcon}
                    sx={{ ...(fullWidth && { width: "100%" }) }}
                    onClose={handleCleanQuery}
                    renderValue={(selected) =>
                        Array.isArray(selected)
                            ? selected.map((select) => options.find((option) => option.id === select)?.name).join(", ")
                            : selected
                    }
                >
                    {withSearchBar && options.length > MIN_OPTIONS_TO_SHOW_SEARCH_BAR && (
                        <SearchBar setSearchQuery={setSearchQuery} className={searchBarClassName} />
                    )}
                    {!!placeholder && (
                        <MenuItem disabled value="" className={classes.menuItemContainer}>
                            {t(`${base}.${placeholder}`)}
                        </MenuItem>
                    )}
                    {options &&
                        options.map((option) => {
                            return (
                                <MenuItem
                                    className={classes.menuItemContainer}
                                    key={option.id}
                                    value={option.id}
                                    style={{
                                        ...(!option.name.toLowerCase().includes(searchQuery.toLowerCase()) && {
                                            display: "none",
                                        }),
                                    }}
                                >
                                    <Box className={classes.menuItem}>
                                        <Checkbox
                                            checked={(dropdownValue || []).indexOf(option.id) > -1}
                                            color="info"
                                        />
                                        {withPicture && option.id !== allElements && (
                                            <Box className={classes.imageContainer}>
                                                {option.picture ? (
                                                    <Avatar
                                                        src={option.picture}
                                                        alt="user avatar"
                                                        sx={{ height: avatarSize, width: avatarSize }}
                                                    />
                                                ) : (
                                                    <Avatar sx={{ height: avatarSize, width: avatarSize }}>
                                                        <AccountCircleIcon />
                                                    </Avatar>
                                                )}
                                            </Box>
                                        )}
                                        <Typography
                                            variant={bigSize ? "bigHMIDropdown" : "subtitle1"}
                                            className={classes.name}
                                        >
                                            {t(`${base}.${optionsHolder}`, { [key]: option.name })}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                </Select>
            </Box>
        );
    }

    return (
        <Box sx={fullWidth ? { width: "100%" } : undefined} className={classes.dropdownMenuContainer}>
            {!hideTitle && (
                <InputLabel id="dropdown-select-label" className={classes.inputLabel}>
                    <Typography variant={bigSize ? "h5" : "subtitle2"}>
                        {t(`${base}.${title}`).toUpperCase()}
                    </Typography>
                </InputLabel>
            )}
            <Select
                multiple={false}
                disabled={disabled}
                displayEmpty={!!placeholder}
                labelId="dropdown-select-label"
                id="dropdown-select"
                className={`${classes.select} ${className || ""}`}
                value={dropdownValue}
                onChange={handleChange}
                IconComponent={KeyboardArrowDownOutlinedIcon}
                sx={{ ...(fullWidth && { width: "100%" }) }}
                onClose={handleCleanQuery}
            >
                {withSearchBar && options.length > MIN_OPTIONS_TO_SHOW_SEARCH_BAR && (
                    <SearchBar setSearchQuery={setSearchQuery} className={searchBarClassName} />
                )}
                {!!placeholder && (
                    <MenuItem disabled value="" className={classes.menuItemContainer}>
                        {t(`${base}.${placeholder}`)}
                    </MenuItem>
                )}
                {options &&
                    options.map((option) => (
                        <MenuItem
                            className={classes.menuItemContainer}
                            key={option.id}
                            value={option.id}
                            style={{
                                ...(!option.name.toLowerCase().includes(searchQuery.toLowerCase()) && {
                                    display: "none",
                                }),
                            }}
                        >
                            <Box className={classes.menuItem}>
                                {withPicture && option.id !== allElements && (
                                    <Box className={classes.imageContainer}>
                                        {option.picture ? (
                                            <Avatar
                                                src={option.picture}
                                                alt="user avatar"
                                                sx={{ height: avatarSize, width: avatarSize }}
                                            />
                                        ) : (
                                            <Avatar sx={{ height: avatarSize, width: avatarSize }}>
                                                <AccountCircleIcon />
                                            </Avatar>
                                        )}
                                    </Box>
                                )}
                                <Typography variant={bigSize ? "bigHMIDropdown" : "subtitle1"} className={classes.name}>
                                    {t(`${base}.${optionsHolder}`, { [key]: option.name })}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
            </Select>
        </Box>
    );
};
